import React, {
  FunctionComponent,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import cloneDeep from 'lodash.clonedeep';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import * as actions from 'data/accessFunction/accessFunction.actions';
import { AccessFunctionFormData, AccessFunctionInterfaceFormSectionType } from 'data/accessFunction/accessFunction.types';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as userSelectors from 'users/users-selectors';
import * as userActions from 'users/users-actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import * as networkSelectors from 'data/network/network.selectors';
import * as networkActions from 'data/network/network.actions';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as sshSecurityInfoSelectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import * as sshSecurityInfoActions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import * as locationSelectors from 'data/location/location.selectors';
import * as locationActions from 'data/location/location.actions';
import * as distributorConfigSelectors from 'data/distributorConfiguration/distributorConfiguration.selectors';
import * as distributorConfigActions from 'data/distributorConfiguration/distributorConfiguration.actions';
import * as distributorConfigAdapters from 'data/distributorConfiguration/distributorConfiguration.adapter';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { DistributorConfigurationTableData } from 'data/distributorConfiguration/distributorConfiguration.types';
import { adaptToForm } from 'data/accessFunction/accessFunction.adapter';
import AccessFunctionForm from 'routes/AccessFunction/components/AccessFunctionForm';
import FieldCurrentState from 'components/FieldCurrentState';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as processOptionSelectors from 'data/processOption/processOption.selectors';
import * as diameterSelectors from 'data/diameter/diameter.selectors';
import * as cspConfigSelectors from 'data/cspConfig/cspConfig.selectors';
import * as cspConfigActions from 'data/cspConfig/cspConfig.actions';
import { TabProp } from 'shared/multi-tab/MultiTab';
import { adaptTemplate5GForGRExisting } from 'routes/AccessFunction/utils/handle5gPOIsforGR';
import {
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  adaptLocationField,
  adaptTemplateInterfaceFields,
  adaptTemplate5GFields,
  adaptTemplatePCRFFields,
  adaptTimezoneField,
  adaptTemplateLabelsForDeployment,
  adaptNetTypeFiled,
  adaptAuditTypeField,
  adaptDiameterField,
  adaptTemplateInterfaceForAXSGPModel,
  adaptDeliveryViaField,
  adaptCcdestipField,
  adaptTemplateX2Interface,
  adaptTemplatesBasedOnAppSettings,
  adaptTemplateDeliveryFormatForEricssonLiims,
  adaptTemplateForNokiaCMG,
  adaptTemplateForNokiaCMGU,
  adaptTemplateForNokiaSMFAdmin,
  adaptTemplateForETSI103221,
  adaptTemplateForUtimaco,
  adaptTemplateMCTypeField,
  adaptTemplateForUtimacoMCTypeChange,
  adaptTemplateAdvancedSettingForEricssonWebService,
  adaptTemplateInterfaceForDeliveryFormat,
  adaptTemplateForAMFIdentifierAssociation
} from '../../utils/accessFunctionTemplate.adapter';
import { useGetDistributorConfigurationsByFilter } from '../../hooks/useGetDistributorConfigurationsByFilter';
import { adaptTemplate4GFieldsForGRExisting } from '../../utils/handle4gPOIsforGR';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  selectedAccessFunction: accessFunctionSelectors.getSelectedAccessFunction(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state),
  filteredDistributorConfigurations: distributorConfigSelectors.getFilteredDistributorConfigurations(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  networks: networkSelectors.getNetworks(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  sshSecurityInfos: sshSecurityInfoSelectors.getSshSecurityInfos(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  countries: locationSelectors.getCountryList(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  xtps: xtpSelectors.getXtps(state),
  xdps: xdpSelectors.getXdps(state),
  processOptions: processOptionSelectors.getProcessOptions(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  is5gcVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  isNplusKGREnabled: supportedFeaturesSelectors.isNplusKGREnabled(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state),
  enabledFeatures: supportedFeaturesSelectors.getEnabledFeatures(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  diameter: diameterSelectors.getDiameter(state),
  cspConfigs: cspConfigSelectors.getCspConfigList(state),
  isIdentifierAssociationExtensionsEnabled: discoverySelectors.isIdentifierAssociationExtensionsEnabled(state),
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
  getDistributorConfigurationsByFilter: distributorConfigActions.getDistributorConfigurationsByFilter,
  goToAccessFunctionList: actions.goToAccessFunctionList,
  goToAccessFunctionEdit: actions.goToAccessFunctionEdit,
  getTimezones: userActions.getTimezones,
  getContacts: contactActions.getAllContacts,
  getNetworks: networkActions.getNetworks,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getSshSecurityInfos: sshSecurityInfoActions.getSshSecurityInfos,
  getXtps: xtpActions.getXtps,
  getXdps: xdpActions.getXdps,
  fetchLocationList: locationActions.fetchLocationList,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
  getCspConfigs: cspConfigActions.getCspConfigList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AccessFunctionView: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedAccessFunction,
  accessFunction,
  filteredDistributorConfigurations,
  timezones,
  contacts,
  networks,
  securityInfos,
  sshSecurityInfos,
  history,
  enabledAccessFunctions,
  countries,
  accessFunctions,
  accessFunctionTemplates,
  xtps,
  xdps,
  processOptions,
  xcipioNodes,
  is5gcVcpEnabled,
  isNplusKGREnabled,
  deployment,
  deliveryFunctionNetworkInterfaces,
  enabledFeatures,
  isLocationRequired,
  diameter,
  cspConfigs,
  isIdentifierAssociationExtensionsEnabled,
  goToAccessFunctionList,
  goToAccessFunctionEdit,
  getAccessFunctionWithViewOptions,
  getTimezones,
  getContacts,
  getNetworks,
  getSecurityInfos,
  getSshSecurityInfos,
  getDistributorConfigurationsByFilter,
  getXtps,
  getXdps,
  fetchLocationList,
  getDfnis,
  getCspConfigs,
}: Props) => {
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<AccessFunctionFormData>>();
  const [distributorConfigsTableData, setDistributorConfigsTableData] =
    useState<DistributorConfigurationTableData[]>([]);
  const { t } = useTranslation();

  const accessFunctionId = selectedAccessFunction[0]?.id;

  const customFieldComponentMap = {
    accessFunctionCurrentState: FieldCurrentState,
  };

  useEffect(() => {
    if (accessFunctionId) {
      getAccessFunctionWithViewOptions(deliveryFunctionId, accessFunctionId);
    }
  }, [accessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    getTimezones();
    getContacts(deliveryFunctionId);
    getNetworks(deliveryFunctionId);
    getSecurityInfos(deliveryFunctionId);
    getSshSecurityInfos(deliveryFunctionId);
    getXtps(deliveryFunctionId);
    getXdps(deliveryFunctionId);
    fetchLocationList();
    getDfnis(deliveryFunctionId);
    getCspConfigs(deliveryFunctionId);
  }, [
    deliveryFunctionId,
    getTimezones,
    getContacts,
    getNetworks,
    getSecurityInfos,
    getSshSecurityInfos,
    getXtps,
    getXdps,
    fetchLocationList,
    getDfnis,
    getCspConfigs,
  ]);

  useEffect(() => {

    let availableTemplates = adaptTemplateOptions(
      accessFunctionTemplates,
      countries,
      timezones,
      contacts,
      networks,
      securityInfos,
      sshSecurityInfos,
      xtps,
      xdps,
      processOptions,
      enabledAccessFunctions,
      xcipioNodes,
      accessFunctions,
      deliveryFunctionNetworkInterfaces,
      enabledFeatures,
      cspConfigs,
    );

    availableTemplates = adaptTemplatesBasedOnAppSettings(
      availableTemplates,
      isLocationRequired,
      isIdentifierAssociationExtensionsEnabled,
    )

    availableTemplates = adaptTemplateX2Interface(
      availableTemplates,
      enabledFeatures,
    )

    setTemplates(availableTemplates);
  }, [
    accessFunctionTemplates,
    countries,
    timezones,
    contacts,
    networks,
    securityInfos,
    sshSecurityInfos,
    xtps,
    xdps,
    processOptions,
    enabledAccessFunctions,
    accessFunctions,
    xcipioNodes,
    deliveryFunctionNetworkInterfaces,
    cspConfigs,
  ]);

  useEffect(() => {
    if (accessFunction != null && templates != null && accessFunctions) {
      const formData = adaptToForm(accessFunction, accessFunctions);
      let templateData = adaptTemplateVisibilityMode(templates[formData.tag], 'view');
      templateData = adaptTemplate5GFields(templateData, is5gcVcpEnabled);
      templateData = adaptTemplateLabelsForDeployment(templateData, deployment);
      templateData = adaptTimezoneField(templateData, timezones, formData);
      templateData = adaptNetTypeFiled(templateData, formData);
      templateData = adaptAuditTypeField(templateData, formData);
      templateData = adaptDiameterField(templateData, diameter, formData, true);
      templateData = adaptTemplatePCRFFields(templateData, 'view');

      // As this is the view mode, it is not required to load the list of states or cities
      // as the input will be read only. So it is just inserting the option that should
      // be displayed as selected.
      if (formData.stateName) {
        templateData = adaptLocationField(templateData, [formData.stateName], 'stateName');
      }
      if (formData.city) {
        templateData = adaptLocationField(templateData, [formData.city], 'city');
      }

      templateData = adaptTemplateInterfaceFields(templateData, formData, xcipioNodes);
      templateData = adaptTemplateInterfaceForAXSGPModel(templateData, 'model', formData.model as string);
      templateData = adaptTemplate4GFieldsForGRExisting(templateData, formData, isNplusKGREnabled, xcipioNodes, networks, templates);
      templateData = adaptTemplate5GForGRExisting(templateData, formData, xcipioNodes, networks, accessFunctions, templates);
      templateData = adaptDeliveryViaField(templateData, formData);
      templateData = adaptCcdestipField(templateData, formData, xcipioNodes);
      templateData = adaptTemplateDeliveryFormatForEricssonLiims(templateData, formData);
      templateData = adaptTemplateForNokiaCMG(templateData, formData?.model, enabledAccessFunctions?.NOKIA_CMG);
      templateData = adaptTemplateForNokiaCMGU(templateData, enabledAccessFunctions?.NOKIA_CMG_U, formData?.model);
      templateData = adaptTemplateForNokiaSMFAdmin(templateData, formData?.model, enabledAccessFunctions?.NOKIA_SMF_ADMIN);
      templateData = adaptTemplateForETSI103221(isIdentifierAssociationExtensionsEnabled, templateData, enabledAccessFunctions?.ETSI103221, formData?.model);
      templateData = adaptTemplateForUtimaco(templateData)
      templateData = adaptTemplateForUtimacoMCTypeChange(templateData, formData)
      templateData = adaptTemplateForAMFIdentifierAssociation(isIdentifierAssociationExtensionsEnabled, templateData, formData?.model)

      // get appprotocolver
      if (formData && formData.provisioningInterfaces && formData?.provisioningInterfaces.length > 0) {
        const appprotocolver = formData?.provisioningInterfaces[0].version;
        templateData = adaptTemplateAdvancedSettingForEricssonWebService(appprotocolver, templateData);
      }
      setData(formData);
      setTemplate(templateData);
    }
  }, [
    accessFunction,
    templates,
    is5gcVcpEnabled,
    timezones,
    deployment,
    accessFunctions,
  ]);

  useGetDistributorConfigurationsByFilter(deliveryFunctionId, template, getDistributorConfigurationsByFilter);

  useEffect(() => {
    const tableData = distributorConfigAdapters.adaptToTableData(filteredDistributorConfigurations);
    setDistributorConfigsTableData(tableData);
  }, [filteredDistributorConfigurations]);

  const handleClickEdit = () => goToAccessFunctionEdit(history);
  const handleCancel = () => goToAccessFunctionList(history);

  if (!template || !data) {
    return null;
  }

  const handleTabChange = (tab: TabProp, formSectionName: AccessFunctionInterfaceFormSectionType) => {
    if (template?.key === 'COGNYTE' ||
      template?.key === 'ERICSSON_LIMS' ||
      template?.key === 'ERICSSON_LIMS_CORBA' ||
      template?.key === 'SIEMENS_LIMS' ||
      template?.key === 'VRNT_SG' ||
      template?.key === 'VIETTEL_MD') {
      let adaptedTemplate: FormTemplate | undefined = cloneDeep(template)
      const clonedData = cloneDeep(data);
      adaptedTemplate.formId = Date.now().toString();

      handleTabChangeDeliveryFormat(tab.panel.props.defaults.deliveryFormat, adaptedTemplate);

      if (tab.panel.props.defaults.mctype) {
        if (template?.key === 'SIEMENS_LIMS') {
          adaptedTemplate = adaptTemplateMCTypeField(adaptedTemplate, 'dataInterfaces', 'mctype', tab.panel.props.defaults.mctype, tab.panel.props.defaults.deliveryFormat)
        }
      }
      setTemplate(adaptedTemplate);
    }
  };

  const handleTabChangeDeliveryFormat = (strDeliveryFormat: string, adaptedTemplate: FormTemplate | undefined) => {
    if (adaptedTemplate) {
      const affectedFields = (adaptedTemplate.dataInterfaces as FormTemplateSection).fields.deliveryFormat.affectedFieldsOnChange;
      if (strDeliveryFormat !== 'TS33108') {
        if (adaptedTemplate.key === 'ERICSSON_LIMS') {
          // handleContentInterfaces('hide', adaptedTemplate);
        }
        adaptTemplateInterfaceForDeliveryFormat(affectedFields, adaptedTemplate.dataInterfaces as FormTemplateSection, 'TS201671', (template as FormTemplate).key);
      } else {
        if (adaptedTemplate.key === 'ERICSSON_LIMS') {
          // handleContentInterfaces('show', adaptedTemplate);
        }
        adaptTemplateInterfaceForDeliveryFormat(affectedFields, adaptedTemplate.dataInterfaces as FormTemplateSection, 'TS33108', (template as FormTemplate).key);
      }
    }
  };

  return (
    <AccessFunctionForm
      title={t('accessFunction.view.title')}
      isEditing={false}
      data={data}
      accessFunctionByType={{}}
      enabledAccessFunctions={enabledAccessFunctions}
      distributorConfigs={distributorConfigsTableData}
      template={template}
      customFieldComponentMap={customFieldComponentMap}
      networks={networks}
      is5gcVcpEnabled={is5gcVcpEnabled}
      shouldValidateOnRender
      onClickEdit={handleClickEdit}
      onClickCancelClose={handleCancel}
      onTabChange={handleTabChange}
      mode="view"
      xcipioNodes={xcipioNodes}
    />
  );
};

export default withRouter(connector(AccessFunctionView));
